import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router';
import { useDashboardContext } from '../../context/DashboardContext';
import { useTranslation } from '../../../../shared/i18n';
import { NavigationItems } from '../NavigationItems';
import { SignOutButton } from '../SignOutButton';
import { LanguageSwitcher } from '../../../../shared/components';
import { ZodiacAvatar } from '../ZodiacAvatar';

// Import the logo - this will be bundled with the app
// Note: In Expo/Metro for web, image imports return an object { uri: 'path' }
// while in Vite/webpack they return a string. We handle both cases.
import powerkidIconImport from '../../../kid-dashboard/assets/powerkid-icon.png';

// Normalize the logo path to always be a string
const powerkidIcon = typeof powerkidIconImport === 'object' && powerkidIconImport?.uri
  ? powerkidIconImport.uri
  : powerkidIconImport;

// Shared styles for navigation
const navLinkClass = ({ isActive }) =>
  `rounded-2 mb-2 d-block text-decoration-none ${isActive ? '' : 'p-3'}`;

const navLinkStyle = ({ isActive }) =>
  isActive
    ? { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '12px 16px' }
    : {};

/**
 * Unified Layout component for both kid and guardian dashboards
 *
 * Features:
 * - Responsive design with mobile hamburger menu
 * - Desktop sidebar navigation
 * - Language switcher
 * - Flash message support
 * - Mode-specific sidebar header (kid profile vs PowerKid branding)
 */
export const Layout = ({
  children,
  flashNotice,
  flashAlert,
  setFlashNotice,
  setFlashAlert,
  buildTime
}) => {
  const { t } = useTranslation();
  const { isKid, userData } = useDashboardContext();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Check for flash messages from sessionStorage on route changes
  useEffect(() => {
    const flash = sessionStorage.getItem('flash_notice');
    if (flash && setFlashNotice) {
      sessionStorage.removeItem('flash_notice');
      setFlashNotice(flash);
    }
  }, [location.pathname, setFlashNotice]);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  // Get child data for kid mode
  const child = isKid ? userData?.child : null;

  return (
    <div className="d-flex flex-column min-vh-100" data-testid={isKid ? 'kid-dashboard' : 'guardian-dashboard'}>
      {/* Top Navbar */}
      <nav className="navbar navbar-expand-lg navbar-dark bg-dark">
        <div className="container-fluid px-3">
          {/* Logo */}
          <a href="/" className="navbar-brand d-flex align-items-center">
            <img src={powerkidIcon} alt="PowerKid Logo" width="30" height="30" className="me-2" />
            <span className="fw-bold">PowerKid</span>
          </a>

          {/* Mobile: Hamburger button */}
          <button
            className="navbar-toggler border-0 d-lg-none"
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle navigation"
            aria-expanded={mobileMenuOpen}
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          {/* Desktop: Language switcher */}
          <div className="d-none d-lg-flex align-items-center">
            <LanguageSwitcher />
          </div>
        </div>
      </nav>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="d-lg-none bg-white border-bottom shadow-sm" data-testid="mobile-menu">
          <div className="p-3">
            {/* Kid profile in mobile menu (only for kid mode) */}
            {isKid && child && (
              <div className="d-flex align-items-center gap-2 mb-3 pb-3 border-bottom">
                <ZodiacAvatar
                  zodiacAnimal={child.zodiac_animal}
                  zodiacName={child.zodiac_name}
                  size={36}
                />
                <span className="fw-bold">{child.username}</span>
              </div>
            )}

            {/* Navigation items */}
            <NavigationItems
              navLinkClass={navLinkClass}
              navLinkStyle={navLinkStyle}
              onItemClick={closeMobileMenu}
            />
            <SignOutButton />

            {/* Language switcher for mobile */}
            <div className="pt-3 mt-3 border-top">
              <LanguageSwitcher isMobile={true} />
            </div>
          </div>
        </div>
      )}

      <div className="d-flex flex-fill bg-light">
        {/* Desktop Sidebar */}
        <aside className="bg-white border-end d-none d-lg-block flex-shrink-0" style={{ width: '256px' }}>
          <div className="p-4">
            {/* Sidebar Header - different for kid vs guardian */}
            <div className="d-flex align-items-center gap-3 mb-5">
              {isKid && child ? (
                <>
                  <ZodiacAvatar
                    zodiacAnimal={child.zodiac_animal}
                    zodiacName={child.zodiac_name}
                  />
                  <h1 className="fw-bold fs-4 mb-0">{child.username}</h1>
                </>
              ) : (
                <>
                  <img src={powerkidIcon} alt="PowerKid Logo" width="32" height="32" />
                  <h1 className="fw-bold fs-4 mb-0">PowerKid</h1>
                </>
              )}
            </div>

            {/* Navigation */}
            <nav>
              <NavigationItems navLinkClass={navLinkClass} navLinkStyle={navLinkStyle} />
              <SignOutButton className="mt-2" />
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className={`flex-fill ${isKid ? 'children-dashboard-main' : ''}`} style={{ overflowY: 'auto' }}>
          {/* Flash Messages */}
          {flashNotice && (
            <div className="container py-3">
              <div className="alert alert-success alert-dismissible fade show" role="alert">
                {flashNotice}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setFlashNotice && setFlashNotice(null)}
                  aria-label="Close"
                ></button>
              </div>
            </div>
          )}
          {flashAlert && (
            <div className="container py-3">
              <div className="alert alert-danger alert-dismissible fade show" role="alert">
                {flashAlert}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setFlashAlert && setFlashAlert(null)}
                  aria-label="Close"
                ></button>
              </div>
            </div>
          )}
          {/* Support both children prop and Outlet for nested routes */}
          {children || <Outlet />}

          {/* Build Time - shown in dev mode to verify latest code is running */}
          {buildTime && (
            <div className="text-center py-2 text-muted small" data-testid="build-time">
              Build: {buildTime}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Layout;
